/**
 * Sidebar navigation.
 *
 * One unified KarroFin admin: seven KarroFin modules followed by the three
 * legacy operations screens that are still in use. This array is the only
 * place nav entries are declared -- routes are unaffected by anything here.
 *
 * Each entry carries three fields that matter:
 *
 *   label       what the user reads in the sidebar
 *   permission  the TabPermission name matched against the logged-in user's
 *               permission list (Drawer.vue renders an item only if it is
 *               present). It is separate from `label` because the KarroFin
 *               permissions are named "KarroFin Dashboard" etc. while the
 *               sidebar should just say "Dashboard".
 *   icon        a Lucide icon name (see components/ui/LucideIcon.vue),
 *               required for the collapsed (mini) sidebar where only icons
 *               are shown.
 *
 * Retired from the sidebar (their routes and pages are untouched and still
 * resolve by URL, and other screens still link into them):
 *   New Customer, Customer, Pre Verification, Business Loan,
 *   LAS, Loan Applied, PL Funnel, Products, Notification Panel, EMI Analysis,
 *   Payment, Review, Service Area, Session History.
 * The legacy Leads list came back out of that group and is the first entry under
 * Operations. It is a different screen from the KarroFin leads one, not an older
 * version of it -- different endpoint, different filters, a row per application
 * rather than per customer -- so the two are surfaced side by side.
 * The KarroFin Leads LIST was replaced by User Management and deleted. Its
 * detail page survives as a route, but nothing links into it any more -- the
 * Dashboard's "Recent offer clicks" table was the last way in and its
 * @row-click was removed, and User Management deliberately opens nothing
 * either, so /karrofin/leads/:id is reachable only by typing the URL (see the
 * note on the route in app-js/router.js). It is still gated on the
 * "KarroFin Leads" TabPermission, so that permission must stay granted even
 * though no sidebar entry carries its name.
 * KarroFin Lenders and KarroFin UTM went further: neither is surfaced anywhere
 * in this panel any more, so those routes and pages were deleted outright.
 * Insurance was retired the same way, but it belongs in a note of its own
 * rather than in the list above: nothing links into it at all. This sidebar
 * entry was its only route in -- a grep for `name: "insurance"` finds the route
 * definition and nothing else -- so pages/insurance is now reachable only by
 * typing /insurance. The route, the page and the server-side insurance
 * endpoints are all untouched, and the "Insurance" TabPermission is left
 * granted; nothing in this panel reads it any more (as with "Enquiry" below).
 * Credit Card and Enquiry went further: both were retired outright, so their
 * routes and their pages (pages/credit-card, pages/Enquiry plus
 * components/enquiry) were deleted rather than just hidden. The server-side
 * "Enquiry" TabPermission and its /loan/enquiry/ endpoints were left in place --
 * nothing in this panel reads them any more.
 * Note Disbursement's detail page lives under pages/new-customer-pages and
 * uses the "Customer details" permission, so that tree must stay in place.
 */
export const namespaced = true;

const COLLAPSED_KEY = "sidebar-collapsed";

export const state = {
  /** Mini (icons-only) sidebar. Persisted so it survives a reload. */
  is_collapsed: localStorage.getItem(COLLAPSED_KEY) === "1",
  items: [
    /* ------------------------------------------------------------ KarroFin */
    {
      label: "Dashboard",
      group: "KarroFin",
      permission: "KarroFin Dashboard",
      icon: "layout-dashboard",
      url: { name: "karrofin-dashboard" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      label: "Products",
      group: "KarroFin",
      permission: "KarroFin Offers",
      icon: "tag",
      url: { name: "karrofin-offers" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      label: "Offer Management",
      group: "KarroFin",
      permission: "KarroFin Applications",
      icon: "clipboard-list",
      url: { name: "karrofin-applications" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      label: "Leads Management",
      // KarroFin, not Operations: it reads as a sibling of Offer Management,
      // directly below it. `group` is not decoration -- Drawer.vue draws a rule
      // and a subheading wherever it CHANGES, so leaving this on "Operations"
      // here would print an Operations heading in the middle of the KarroFin
      // block and a second one again at Insurance.
      group: "KarroFin",
      // The pre-KarroFin leads list, pages/Leads/index.vue. Still its own
      // screen, not a view of User Management: its own GET_LEAD_LIST endpoint,
      // and a row per APPLICATION where User Management has a row per customer.
      //
      // There is no "Leads" TabPermission -- the server only ever had
      // "KarroFin Leads" (team/management/commands/seed_karrofin_permissions.py),
      // and this page predates the permission scheme entirely. Minting a new one
      // would hide this entry from every existing user until an admin granted
      // it, so it rides the same permission the KarroFin leads screen does.
      // Anyone who can see that screen can see this one.
      permission: "KarroFin Leads",
      icon: "inbox",
      url: { name: "leads" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      label: "User Management",
      group: "KarroFin",
      // Gated on "KarroFin Leads", not on a permission of its own. This screen
      // replaced Leads and its detail page still uses that permission, so
      // riding on it is accurate.
      //
      // (The permission list used to be a login-time snapshot in
      // sessionStorage, which meant a granted permission stayed invisible
      // until the user signed in again. Base.vue now re-reads it from
      // /team/my-access/ on every page load, so a change reaches them on their
      // next navigation.)
      permission: "KarroFin Leads",
      icon: "users",
      url: { name: "karrofin-users" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      label: "Notifications",
      group: "KarroFin",
      permission: "KarroFin Notifications",
      icon: "bell",
      url: { name: "karrofin-notifications" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      // A campaign is a hand-picked slice of the catalogue behind one public
      // link. It lived on Notifications while it was just a button; it has its
      // own screen now because the links have to be findable again afterwards.
      label: "Campaign URL",
      group: "KarroFin",
      // The LABEL was renamed, not this: the permission is a seeded
      // TabPermission row and renaming it here hides the screen from everyone.
      permission: "KarroFin Campaigns",
      icon: "megaphone",
      url: { name: "karrofin-campaigns" },
      is_active_tab: false,
      is_django_tab: false,
    },

    /* ---------------------------------------------------------- Operations */
    // Drawer.vue detects the change of `group` and draws a rule plus an
    // "Operations" subheader here -- only the rule when collapsed.
    {
      // Every message the growth agent and the event messages sent or
      // proposed. Permission string kept from the old Automation screen.
      label: "Notification History",
      group: "KarroFin",
      permission: "KarroFin Automation",
      icon: "clock",
      url: { name: "karrofin-automation-history" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      // Who the growth agent messages each day, with which message, and why.
      // Same permission as the Notification History.
      label: "Agent Tracker",
      group: "KarroFin",
      permission: "KarroFin Automation",
      icon: "bot",
      url: { name: "karrofin-agent-tracker" },
      is_active_tab: false,
      is_django_tab: false,
    },
    // Inside the contiguous "KarroFin" block on purpose: Drawer.vue draws a
    // divider and subheader wherever `group` changes, so an entry outside it
    // would print a second "KarroFin" heading.
    {
      label: "Template Library",
      group: "KarroFin",
      permission: "KarroFin Templates",
      icon: "file-text",
      url: { name: "karrofin-templates" },
      is_active_tab: false,
      is_django_tab: false,
    },

    /* ---------------------------------------------------------- Operations */
    // Drawer.vue detects the change of `group` and draws a rule plus an
    // "Operations" subheader here -- only the rule when collapsed.
    {

      label: "Disbursement",
      group: "Operations",
      permission: "Disbursement",
      icon: "banknote",
      url: { name: "disbursement" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      label: "Team",
      group: "Operations",
      permission: "Team",
      icon: "users",
      url: { name: "team" },
      is_active_tab: false,
      is_django_tab: false,
    },
    {
      label: "Reports",
      group: "Operations",
      permission: "Reports",
      icon: "chart-column",
      url: { name: "reports" },
      is_active_tab: false,
      is_django_tab: false,
    },
  ],
};

export const mutations = {
  SET_ACTIVE_TAB(state, index) {
    localStorage.setItem("active-tab", index);

    if (index != -1) {
      state.items[0].is_active_tab = true;
    }
  },
  SET_COLLAPSED(state, value) {
    state.is_collapsed = !!value;
    localStorage.setItem(COLLAPSED_KEY, value ? "1" : "0");
  },
};

export const actions = {
  setActiveTab({ commit }, index) {
    commit("SET_ACTIVE_TAB", index);
  },
  setCollapsed({ commit }, value) {
    commit("SET_COLLAPSED", value);
  },
  toggleCollapsed({ commit, state }) {
    commit("SET_COLLAPSED", !state.is_collapsed);
  },
};

export const getters = {
  getDrawerItems: (state) => {
    return state.items;
  },
  isCollapsed: (state) => {
    return state.is_collapsed;
  },
  // Looked up by label rather than by a hard index: the old `items[9]` broke
  // silently whenever the list changed length. Mapped in a few detail pages,
  // rendered by none of them.
  getManageTab: (state) => {
    return state.items.find((item) => item.label === "Team") || null;
  },
};
